import { useState, useEffect, useCallback, useRef } from 'react';

// Event name for notifying other components when yt-dlp is updated
export const YTDLP_UPDATED_EVENT = 'ytdlp-updated';

export interface YtDlpLastResult {
  status: 'updated' | 'up-to-date' | 'skipped' | 'error';
  message?: string;
  version?: string;
}

export interface YtDlpVersionInfo {
  currentVersion: string | null;
  latestVersion: string | null;
  updateAvailable: boolean;
  lastChecked: string | null;
  lastUpdated: string | null;
  lastResult: YtDlpLastResult | null;
}

const EMPTY_VERSION_INFO: YtDlpVersionInfo = {
  currentVersion: null,
  latestVersion: null,
  updateAvailable: false,
  lastChecked: null,
  lastUpdated: null,
  lastResult: null,
};

export type YtDlpUpdateStatus = 'idle' | 'checking' | 'updating' | 'success' | 'error';

interface UseYtDlpUpdateResult {
  versionInfo: YtDlpVersionInfo;
  updateStatus: YtDlpUpdateStatus;
  checkingVersion: boolean;
  errorMessage: string | null;
  successMessage: string | null;
  checkLatestVersion: () => Promise<void>;
  performUpdate: () => Promise<void>;
  clearMessages: () => void;
}

export function useYtDlpUpdate(token: string | null): UseYtDlpUpdateResult {
  const [versionInfo, setVersionInfo] = useState<YtDlpVersionInfo>(EMPTY_VERSION_INFO);
  const [updateStatus, setUpdateStatus] = useState<YtDlpUpdateStatus>('idle');
  const [checkingVersion, setCheckingVersion] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Track mounted state to prevent state updates after unmount
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const clearMessages = useCallback(() => {
    setErrorMessage(null);
    setSuccessMessage(null);
  }, []);

  const checkLatestVersion = useCallback(async () => {
    if (!token) {
      return;
    }

    setCheckingVersion(true);
    setUpdateStatus('checking');
    clearMessages();

    try {
      const response = await fetch('/api/ytdlp/latest-version', {
        headers: {
          'x-access-token': token,
        },
      });

      if (!mountedRef.current) return;

      if (!response.ok) {
        throw new Error('Failed to fetch version information');
      }

      const data = await response.json();

      if (!mountedRef.current) return;

      setVersionInfo({
        currentVersion: data.currentVersion,
        latestVersion: data.latestVersion,
        updateAvailable: data.updateAvailable,
        lastChecked: data.lastChecked ?? null,
        lastUpdated: data.lastUpdated ?? null,
        lastResult: data.lastResult ?? null,
      });
      setUpdateStatus('idle');
    } catch (err) {
      if (!mountedRef.current) return;
      setErrorMessage(err instanceof Error ? err.message : 'Failed to check version');
      setUpdateStatus('error');
    } finally {
      if (mountedRef.current) {
        setCheckingVersion(false);
      }
    }
  }, [token, clearMessages]);

  const performUpdate = useCallback(async () => {
    if (!token) {
      return;
    }

    setUpdateStatus('updating');
    clearMessages();

    try {
      const response = await fetch('/api/ytdlp/update', {
        method: 'POST',
        headers: {
          'x-access-token': token,
        },
      });

      const data = await response.json();

      if (!mountedRef.current) return;

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Update failed');
      }

      setSuccessMessage(data.message);
      setUpdateStatus('success');

      // Dispatch event to notify other components (e.g., App.tsx header)
      window.dispatchEvent(new CustomEvent(YTDLP_UPDATED_EVENT));

      // Refresh version info after successful update
      await checkLatestVersion();
    } catch (err) {
      if (!mountedRef.current) return;
      setErrorMessage(err instanceof Error ? err.message : 'Update failed');
      setUpdateStatus('error');
    }
  }, [token, clearMessages, checkLatestVersion]);

  // Auto-check version on mount
  useEffect(() => {
    if (token) {
      checkLatestVersion();
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    versionInfo,
    updateStatus,
    checkingVersion,
    errorMessage,
    successMessage,
    checkLatestVersion,
    performUpdate,
    clearMessages,
  };
}
